import {useCallback, useRef, useState, type ReactNode} from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@kroll-resolver-crisp/ui-components-react';

export type ConfirmOptions = {
  readonly title: string;
  readonly description?: ReactNode;
  // The action button's label. Defaults to "Delete", since most confirms gate a delete.
  readonly confirmLabel?: string;
  // Paint the action destructive. Defaults to true; pass false for a confirm that isn't a loss.
  readonly destructive?: boolean;
};

// The AlertDialog confirmation as an awaitable, in place of `globalThis.confirm`: a handler awaits
// `confirm({...})` exactly where it used to call the browser's, and the caller renders
// `confirmDialog` once. Dismissing (Cancel, Escape, the overlay) resolves false.
export function useConfirm() {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolveRef = useRef<((confirmed: boolean) => void) | null>(null);

  const settle = useCallback((confirmed: boolean) => {
    resolveRef.current?.(confirmed);
    resolveRef.current = null;
    setOptions(null);
  }, []);

  const confirm = useCallback(
    async (next: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        // A second confirm replaces the first, which counts as declined.
        resolveRef.current?.(false);
        resolveRef.current = resolve;
        setOptions(next);
      }),
    []
  );

  const confirmDialog = (
    <AlertDialog
      open={options !== null}
      onOpenChange={(open) => {
        if (!open) {
          settle(false);
        }
      }}>
      <AlertDialogContent size="sm">
        {options ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>{options.title}</AlertDialogTitle>
              {options.description ? (
                <AlertDialogDescription>{options.description}</AlertDialogDescription>
              ) : null}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className={
                  options.destructive === false
                    ? undefined
                    : 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                }
                onClick={() => {
                  settle(true);
                }}>
                {options.confirmLabel ?? 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        ) : null}
      </AlertDialogContent>
    </AlertDialog>
  );

  return {confirm, confirmDialog};
}
